import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ClipboardCheck, AlertTriangle, Clock, PenLine, ArrowUpRight, UserCheck, Hand, Download, Star, Save, Pencil, X, Users, Copy, Upload, RefreshCw } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ReviewerPickerDialog from "@/components/deals/ReviewerPickerDialog";

const BUCKETS = [
  { key: "re_review_required", label: "Re-Review Required", icon: AlertTriangle, tone: "red",
    desc: "Material deal terms changed after legal review — prior approval invalidated." },
  { key: "overdue_attention", label: "Overdue / Attention Needed", icon: Clock, tone: "amber",
    desc: "Awaiting review past the threshold, or a deposit discrepancy needs resolution." },
  { key: "awaiting_initial_review", label: "Awaiting Initial Review", icon: ClipboardCheck, tone: "blue",
    desc: "Draft contracts waiting for a first legal / compliance sign-off." },
  { key: "approved_awaiting_signature", label: "Approved / Awaiting Signature", icon: PenLine, tone: "emerald",
    desc: "Legally reviewed and moving through the (simulated) signature flow." },
];

const TONE = {
  red: "border-red-200 bg-red-50 text-red-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const SELECT_CLS = "h-9 px-2 rounded-md border border-slate-300 text-sm bg-white";
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const EMPTY_FILTERS = { reviewer: "", priority: "", min_age_days: "", status: "", overdue: "" };

function PriorityBadge({ priority }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
      priority === "high" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>
      {priority}
    </span>
  );
}

export default function ReviewerWorkload() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignFor, setAssignFor] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [filters, setFilters] = useState(() => ({
    reviewer: searchParams.get("reviewer") || "",
    priority: searchParams.get("priority") || "",
    min_age_days: searchParams.get("min_age_days") || "",
    status: searchParams.get("status") || "",
    overdue: searchParams.get("overdue") || "",
  }));
  const [reviewers, setReviewers] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [teamViews, setTeamViews] = useState([]);
  const [canPublish, setCanPublish] = useState(false);
  const [viewCounts, setViewCounts] = useState(null); // null = loading
  const [summary, setSummary] = useState(null);
  const [countsError, setCountsError] = useState(false);
  const [countsUpdatedAt, setCountsUpdatedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [digestCfg, setDigestCfg] = useState(null);
  const [digestEdit, setDigestEdit] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const defaultAppliedRef = useRef(false);

  // Keep the URL in sync so a deep-linked filter (e.g. from the weekly digest) is clear & reversible.
  useEffect(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") p[k] = v; });
    setSearchParams(p, { replace: true });
  }, [filters, setSearchParams]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    api.get("/contracts/reviewer-workload", { params })
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not load workload"))
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  // Seniors can filter by any reviewer; fetch the eligible list once.
  useEffect(() => {
    api.get("/contracts/eligible-reviewers")
      .then(({ data }) => setReviewers(data.reviewers || []))
      .catch(() => {}); // 403 for non-senior — silently ignore, the dropdown still offers me/unassigned
  }, []);

  const loadCounts = useCallback(() => {
    api.get("/contracts/reviewer-workload/view-counts")
      .then(({ data }) => { setViewCounts(data.counts || {}); setSummary(data.summary || null); setCountsError(false); setCountsUpdatedAt(Date.now()); })
      .catch(() => setCountsError(true)); // keep last-good counts; flag error, never flash stale as authoritative
  }, []);

  // Light auto-refresh: conservative interval, paused while the tab is hidden, and an immediate
  // refresh when the tab regains focus. Reuses the single authorized count query (no extra polling
  // of notifications/auth/workload endpoints).
  useEffect(() => {
    const REFRESH_MS = 90000;
    let timer = null;
    const tick = () => { if (!document.hidden) loadCounts(); };
    timer = setInterval(tick, REFRESH_MS);
    const onVis = () => { if (!document.hidden) loadCounts(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [loadCounts]);

  // Light 15s tick to keep the "updated Xs ago" label current (no network calls).
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const loadViews = useCallback(() => {
    api.get("/contracts/reviewer-workload/views")
      .then(({ data }) => {
        const personal = data.views || [];
        setSavedViews(personal);
        setTeamViews(data.team_views || []);
        setCanPublish(!!data.can_publish);
        // Apply the user's default view once, only when arriving without explicit URL filters.
        if (!defaultAppliedRef.current) {
          defaultAppliedRef.current = true;
          const hasParams = Array.from(searchParams.keys()).length > 0;
          const def = personal.find((v) => v.is_default);
          if (!hasParams && def) setFilters({ ...EMPTY_FILTERS, ...(def.filters || {}) });
        }
      })
      .catch(() => {});
    loadCounts();
  }, [searchParams, loadCounts]);
  useEffect(() => { loadViews(); }, [loadViews]);

  const applyView = (v) => setFilters({ ...EMPTY_FILTERS, ...(v.filters || {}) });

  const currentFilterObj = () => {
    const f = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") f[k] = v; });
    return f;
  };

  const saveCurrentView = async () => {
    const name = window.prompt("Name this view (e.g. My Re-Reviews, Overdue > 7 Days)");
    if (!name || !name.trim()) return;
    try {
      await api.post("/contracts/reviewer-workload/views", { name: name.trim(), filters: currentFilterObj() });
      toast.success(`Saved view "${name.trim()}"`);
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save view"); }
  };

  const renameView = async (v) => {
    const name = window.prompt("Rename view", v.name);
    if (!name || !name.trim() || name.trim() === v.name) return;
    try {
      await api.patch(`/contracts/reviewer-workload/views/${v.view_id}`, { name: name.trim() });
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not rename"); }
  };

  const deleteView = async (v) => {
    try {
      await api.delete(`/contracts/reviewer-workload/views/${v.view_id}`);
      toast.success("View deleted");
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete"); }
  };

  const toggleDefault = async (v) => {
    try {
      await api.patch(`/contracts/reviewer-workload/views/${v.view_id}`, { is_default: !v.is_default });
      toast.success(v.is_default ? "Default cleared" : `"${v.name}" is now your default`);
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update default"); }
  };

  const publishTeamView = async () => {
    const name = window.prompt("Publish current filters as a TEAM view. Name:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/contracts/reviewer-workload/team-views", { name: name.trim(), filters: currentFilterObj() });
      toast.success(`Published team view "${name.trim()}"`);
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not publish"); }
  };

  const unpublishTeamView = async (v) => {
    try {
      await api.delete(`/contracts/reviewer-workload/team-views/${v.view_id}`);
      toast.success("Team view unpublished");
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not unpublish"); }
  };

  const copyTeamView = async (v) => {
    try {
      await api.post(`/contracts/reviewer-workload/team-views/${v.view_id}/copy`);
      toast.success("Copied to your saved views");
      loadViews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not copy"); }
  };

  const loadDigest = useCallback(() => {
    api.get("/contracts/digest-schedule")
      .then(({ data }) => setDigestCfg(data))
      .catch(() => {}); // 403 for non-senior/admin — silently ignore
  }, []);
  useEffect(() => { loadDigest(); }, [loadDigest]);

  const saveDigest = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const [hour, minute] = String(fd.get("time") || "09:00").split(":").map((n) => parseInt(n, 10));
    try {
      const { data } = await api.put("/contracts/digest-schedule", {
        weekday: parseInt(fd.get("weekday"), 10), hour, minute, timezone: fd.get("timezone") || "UTC" });
      setDigestCfg(data); setDigestEdit(false);
      toast.success("Digest schedule updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update schedule"); }
  };

  const loadDigestPreview = () => {
    api.get("/contracts/digest-preview")
      .then(({ data }) => setDigestPreview(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not load preview"));
  };

  const [digestOptOut, setDigestOptOut] = useState(false);
  useEffect(() => {
    api.get("/contracts/digest-preference").then(({ data }) => setDigestOptOut(!!data.opt_out)).catch(() => {});
  }, []);
  const toggleDigestOptOut = async () => {
    try {
      const { data } = await api.put("/contracts/digest-preference", { opt_out: !digestOptOut });
      setDigestOptOut(data.opt_out);
      toast.success(data.opt_out ? "You'll no longer receive the weekly digest" : "Weekly digest re-enabled");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update preference"); }
  };

  const openReview = (item) =>
    navigate(`/deals?deal=${item.deal_id}&tab=contracts&ref=${item.contract_id}`);

  const claim = async (item) => {
    setClaiming(item.contract_id);
    try {
      await api.post(`/contracts/${item.contract_id}/claim`);
      toast.success("Contract claimed — it's now assigned to you");
      load(); loadCounts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not claim");
    } finally { setClaiming(null); }
  };

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const isSenior = data?.role_scope === "senior";
  const activeFilters = Object.values(filters).some((v) => v !== "");

  const CountBadge = ({ id }) => {
    if (countsError) return <span data-testid={`view-count-${id}`} className="text-[10px] font-bold text-red-400" title="count unavailable">!</span>;
    if (viewCounts === null) return <span data-testid={`view-count-${id}`} className="text-[10px] text-slate-300">…</span>;
    const c = viewCounts[id];
    if (c === undefined) return null;
    return <span data-testid={`view-count-${id}`}
      className={`rounded-full px-1.5 text-[10px] font-bold ${c === 0 ? "bg-slate-100 text-slate-400" : "bg-[#2563EB] text-white"}`}>{c}</span>;
  };

  const exportCsv = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
      const res = await api.get("/contracts/reviewer-workload/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `reviewer_workload_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Workload exported to CSV");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Export failed"); }
  };

  if (loading && !data) {
    return <AppShell title="Reviewer Workload"><div className="h-40 rounded-xl bg-white border border-slate-200 animate-pulse" /></AppShell>;
  }

  const buckets = data?.buckets || {};
  const counts = data?.counts || {};

  return (
    <AppShell title="Reviewer Workload"
      subtitle="Your legal / compliance review queue — assembled from live contract status, assignments and the Deal Terms Guard.">
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600" data-testid="workload-scope">
          {isSenior ? "Team workload (senior)" : "My assignments + unassigned queue"}
        </span>
        <span className="text-slate-400">Overdue after {data?.overdue_days ?? 3} days · {data?.total ?? 0} shown</span>
        {isSenior && (
          <button data-testid="workload-export-btn" onClick={exportCsv}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
            <Download size={13} /> Export CSV
          </button>
        )}
      </div>

      <div className="mb-5 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="my-queue-summary">
        {[
          { key: "assigned_to_me", label: "Assigned to Me", params: { reviewer: "me" }, tone: "text-[#0A0F1C]" },
          { key: "re_review_required", label: "Re-Review Required", params: { status: "re_review_required" }, tone: "text-red-600" },
          { key: "overdue", label: "Overdue", params: { overdue: "true" }, tone: "text-amber-600" },
          { key: "claimable_now", label: "Claimable Now", params: { reviewer: "unassigned" }, tone: "text-emerald-700" },
        ].map((s) => (
          <button key={s.key} data-testid={`summary-${s.key}`} onClick={() => setFilters({ ...EMPTY_FILTERS, ...s.params })}
            className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-[#2563EB] transition-colors">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{s.label}</span>
            <span className={`font-mono text-lg font-bold ${s.tone}`}>
              {countsError && summary === null ? "!" : summary === null ? "…" : (summary[s.key] ?? 0)}
            </span>
          </button>
        ))}
        <div className="col-span-2 md:col-span-4 flex items-center justify-end gap-2">
          <span data-testid="counts-freshness" className={`text-[10px] ${countsError ? "text-amber-600 font-medium" : "text-slate-400"}`}>
            {countsError
              ? "⚠ Stale — last refresh failed"
              : countsUpdatedAt ? `Updated ${Math.max(0, Math.round((nowTick - countsUpdatedAt) / 1000))}s ago` : "—"}
          </span>
          <button data-testid="counts-refresh" onClick={loadCounts} title="Refresh counts"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-[#2563EB]">
            <RefreshCw size={11} className={countsError ? "text-amber-500" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-2" data-testid="workload-filters">
        <label className="text-xs">
          <span className="mb-0.5 block font-medium text-slate-500">Reviewer</span>
          <select data-testid="filter-reviewer" className={SELECT_CLS} value={filters.reviewer} onChange={setF("reviewer")}>
            <option value="">All</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
            {isSenior && reviewers.map((r) => <option key={r.user_id} value={r.user_id}>{r.name || r.email}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-0.5 block font-medium text-slate-500">Priority</span>
          <select data-testid="filter-priority" className={SELECT_CLS} value={filters.priority} onChange={setF("priority")}>
            <option value="">Any</option><option value="high">High</option><option value="normal">Normal</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-0.5 block font-medium text-slate-500">Awaiting ≥</span>
          <select data-testid="filter-age" className={SELECT_CLS} value={filters.min_age_days} onChange={setF("min_age_days")}>
            <option value="">Any age</option><option value="1">1 day</option><option value="3">3 days</option>
            <option value="7">7 days</option><option value="14">14 days</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-0.5 block font-medium text-slate-500">Bucket</span>
          <select data-testid="filter-status" className={SELECT_CLS} value={filters.status} onChange={setF("status")}>
            <option value="">All</option>
            {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
        <label className="flex h-9 items-center gap-1.5 text-xs font-medium text-slate-600">
          <input type="checkbox" data-testid="filter-overdue" checked={filters.overdue === "true"}
            onChange={(e) => setFilters((f) => ({ ...f, overdue: e.target.checked ? "true" : "" }))} /> Overdue only
        </label>
        {activeFilters && (
          <button data-testid="filter-clear" onClick={() => setFilters(EMPTY_FILTERS)}
            className="h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-600">Clear filters</button>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="saved-views">
        <span className="text-xs font-medium text-slate-400 inline-flex items-center gap-1"><Star size={12} /> My views:</span>
        {savedViews.length === 0 && <span className="text-xs text-slate-300">none yet</span>}
        {savedViews.map((v) => (
          <span key={v.view_id} data-testid={`saved-view-${v.view_id}`}
            className={`group inline-flex items-center gap-1 rounded-full border pl-2.5 pr-1.5 py-1 text-xs ${v.is_default ? "border-[#2563EB] bg-blue-50 text-[#2563EB]" : "border-slate-300 bg-white text-slate-600"}`}>
            <button onClick={() => toggleDefault(v)} data-testid={`saved-view-default-${v.view_id}`} title={v.is_default ? "Default view" : "Set as default"}
              className={v.is_default ? "text-[#2563EB]" : "text-slate-300 hover:text-amber-500"}>
              <Star size={12} fill={v.is_default ? "currentColor" : "none"} />
            </button>
            <button onClick={() => applyView(v)} data-testid={`saved-view-apply-${v.view_id}`} className="font-medium hover:underline">{v.name}</button>
            <CountBadge id={v.view_id} />
            <button onClick={() => renameView(v)} data-testid={`saved-view-rename-${v.view_id}`} className="text-slate-300 hover:text-slate-600"><Pencil size={11} /></button>
            <button onClick={() => deleteView(v)} data-testid={`saved-view-delete-${v.view_id}`} className="text-slate-300 hover:text-red-600"><X size={12} /></button>
          </span>
        ))}
        {activeFilters && (
          <button data-testid="saved-view-save" onClick={saveCurrentView}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs font-medium text-slate-500 hover:border-[#2563EB] hover:text-[#2563EB]">
            <Save size={12} /> Save current view
          </button>
        )}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="team-views">
        <span className="text-xs font-medium text-slate-400 inline-flex items-center gap-1"><Users size={12} /> Team views:</span>
        {teamViews.length === 0 && <span className="text-xs text-slate-300">none published</span>}
        {teamViews.map((v) => (
          <span key={v.view_id} data-testid={`team-view-${v.view_id}`}
            title={`Published by ${v.published_by?.name || "—"} · ${v.published_at ? new Date(v.published_at).toLocaleString() : ""}${v.updated_at ? " · updated " + new Date(v.updated_at).toLocaleDateString() : ""}`}
            className="group inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 pl-3 pr-1.5 py-1 text-xs text-emerald-800">
            <button onClick={() => applyView(v)} data-testid={`team-view-apply-${v.view_id}`} className="font-medium hover:underline">{v.name}</button>
            <CountBadge id={v.view_id} />
            <button onClick={() => copyTeamView(v)} data-testid={`team-view-copy-${v.view_id}`} title="Copy to my views" className="text-emerald-500 hover:text-emerald-900"><Copy size={11} /></button>
            {canPublish && (
              <button onClick={() => unpublishTeamView(v)} data-testid={`team-view-unpublish-${v.view_id}`} title="Unpublish" className="text-emerald-400 hover:text-red-600"><X size={12} /></button>
            )}
          </span>
        ))}
        {canPublish && activeFilters && (
          <button data-testid="team-view-publish" onClick={publishTeamView}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-emerald-400 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
            <Upload size={12} /> Publish current as team view
          </button>
        )}
      </div>

      {canPublish && digestCfg && (
        <div className="mb-6 flex flex-wrap items-center gap-2 text-xs" data-testid="digest-schedule">
          <span className="font-medium text-slate-400">Weekly digest:</span>
          <span className="text-slate-600">{WEEKDAYS[digestCfg.schedule.weekday]} {String(digestCfg.schedule.hour).padStart(2, "0")}:{String(digestCfg.schedule.minute).padStart(2, "0")} {digestCfg.schedule.timezone}</span>
          <span className="text-slate-400">· next {new Date(digestCfg.next_send_at).toLocaleString()}</span>
          <button data-testid="digest-edit" onClick={() => setDigestEdit(!digestEdit)} className="font-medium text-[#2563EB] hover:underline">{digestEdit ? "cancel" : "Edit"}</button>
          <button data-testid="digest-preview-btn" onClick={() => { setDigestPreview(digestPreview ? null : "loading"); if (!digestPreview) loadDigestPreview(); }} className="font-medium text-slate-500 hover:underline">{digestPreview ? "hide preview" : "Preview next digest"}</button>
          <label className="ml-2 inline-flex items-center gap-1 text-slate-500" data-testid="digest-optin">
            <input type="checkbox" data-testid="digest-optin-toggle" checked={!digestOptOut} onChange={toggleDigestOptOut} /> receive in my bell
          </label>
          {digestEdit && (
            <form onSubmit={saveDigest} className="flex flex-wrap items-center gap-1.5" data-testid="digest-form">
              <select name="weekday" defaultValue={digestCfg.schedule.weekday} className="h-8 rounded border border-slate-300 px-1 text-xs">
                {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
              </select>
              <input name="time" type="time" defaultValue={`${String(digestCfg.schedule.hour).padStart(2, "0")}:${String(digestCfg.schedule.minute).padStart(2, "0")}`} className="h-8 rounded border border-slate-300 px-1 text-xs" />
              <input name="timezone" defaultValue={digestCfg.schedule.timezone} placeholder="IANA tz e.g. Europe/London" className="h-8 w-40 rounded border border-slate-300 px-2 text-xs" />
              <button type="submit" data-testid="digest-save" className="h-8 rounded bg-[#0A0F1C] px-3 text-xs font-medium text-white">Save</button>
            </form>
          )}
        </div>
      )}

      {digestPreview && digestPreview !== "loading" && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="digest-preview-panel">
          <div className="mb-2 flex items-center justify-between">
            <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">Preview — not sent</span>
            <button data-testid="digest-preview-refresh" onClick={loadDigestPreview} className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-[#2563EB]"><RefreshCw size={11} /> Refresh</button>
          </div>
          <p className="mb-2 text-sm text-[#0A0F1C]">{digestPreview.text}</p>
          <div className="flex flex-wrap gap-1.5">
            {[
              ["Open", digestPreview.snapshot.total, digestPreview.links.open],
              ["Awaiting", digestPreview.snapshot.counts.awaiting_initial_review, digestPreview.links.awaiting_initial_review],
              ["Re-review", digestPreview.snapshot.counts.re_review_required, digestPreview.links.re_review_required],
              ["Overdue", digestPreview.snapshot.counts.overdue_attention, digestPreview.links.overdue],
              ["Claimable", digestPreview.snapshot.claimable_now, digestPreview.links.claimable_now],
              ["Approved", digestPreview.snapshot.counts.approved_awaiting_signature, digestPreview.links.approved_awaiting_signature],
              ["High risk", digestPreview.snapshot.high_priority, digestPreview.links.high_priority],
            ].map(([label, n, href]) => (
              <button key={label} onClick={() => navigate(href)}
                className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB]">{label} {n ?? 0}</button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Oldest outstanding: {digestPreview.snapshot.oldest} · sends {WEEKDAYS[digestPreview.schedule.weekday]} {String(digestPreview.schedule.hour).padStart(2, "0")}:{String(digestPreview.schedule.minute).padStart(2, "0")} {digestPreview.schedule.timezone} · next {new Date(digestPreview.next_send_at).toLocaleString()}</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {BUCKETS.map((b) => (
          <div key={b.key} data-testid={`workload-stat-${b.key}`} className={`rounded-xl border p-4 ${TONE[b.tone]}`}>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide"><b.icon size={13} /> {b.label}</p>
            <p className="mt-1 font-mono text-2xl font-bold">{counts[b.key] ?? 0}</p>
          </div>
        ))}
      </div>

      {(data?.total ?? 0) === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="workload-empty">
          {activeFilters ? "No contracts match these filters." : "Nothing in your review queue right now."}
        </div>
      )}

      <div className="space-y-8">
        {BUCKETS.map((b) => {
          const items = buckets[b.key] || [];
          if (items.length === 0) return null;
          const Icon = b.icon;
          return (
            <section key={b.key} data-testid={`workload-bucket-${b.key}`}>
              <div className="mb-2">
                <h3 className="font-heading text-lg font-bold text-[#0A0F1C] flex items-center gap-2"><Icon size={18} /> {b.label} <span className="text-slate-400 font-mono text-sm">({items.length})</span></h3>
                <p className="text-xs text-slate-400">{b.desc}</p>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="p-3">Contract</th><th className="p-3">Parties</th><th className="p-3">Reviewer</th>
                    <th className="p-3">Reason / changed terms</th><th className="p-3">Age</th><th className="p-3">Priority</th><th className="p-3"></th>
                  </tr></thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.contract_id} data-testid={`workload-row-${it.contract_id}`} className="border-b border-slate-50 last:border-0 align-top">
                        <td className="p-3">
                          <div className="font-medium text-[#0A0F1C]">{it.title} <span className="text-slate-400">v{it.version}</span></div>
                          <div className="text-[11px] text-slate-400">{it.status?.replace(/_/g, " ")} · deal {it.deal_id?.slice(0, 12)}…</div>
                        </td>
                        <td className="p-3 text-slate-600">
                          <div>B: {it.buyer}</div>
                          <div>S: {it.seller}</div>
                        </td>
                        <td className="p-3 text-slate-600">
                          {it.assigned_reviewer
                            ? <span>{it.assigned_reviewer.name || it.assigned_reviewer.user_id}<div className="text-[11px] text-slate-400">{it.assigned_reviewer.role?.replace(/_/g, " ")}</div></span>
                            : <span className="text-amber-600 font-medium">Unassigned</span>}
                        </td>
                        <td className="p-3 text-slate-600 max-w-[240px]">
                          {it.review_reason || <span className="text-slate-300">—</span>}
                          {it.changed_terms?.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {it.changed_terms.map((t) => <span key={t} className="rounded bg-red-100 px-1 text-[10px] font-semibold text-red-700">{t.replace(/_/g, " ")}</span>)}
                            </div>
                          )}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          <span className={it.overdue ? "font-semibold text-red-600" : "text-slate-500"}>{it.age_days}d{it.overdue ? " ⚠" : ""}</span>
                        </td>
                        <td className="p-3"><PriorityBadge priority={it.priority} /></td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <button onClick={() => openReview(it)} data-testid={`workload-review-${it.contract_id}`}
                            className="h-8 px-3 rounded-md bg-[#0A0F1C] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#1E293B]">
                            Review <ArrowUpRight size={13} />
                          </button>
                          {it.unassigned && (
                            <button onClick={() => claim(it)} disabled={claiming === it.contract_id} data-testid={`workload-claim-${it.contract_id}`}
                              className="ml-1.5 h-8 px-2.5 rounded-md bg-[#047857] text-white text-xs inline-flex items-center gap-1 disabled:opacity-60">
                              {claiming === it.contract_id ? <Loader2 size={13} className="animate-spin" /> : <Hand size={13} />} Claim
                            </button>
                          )}
                          {isSenior && (
                            <button onClick={() => setAssignFor(it)} data-testid={`workload-assign-${it.contract_id}`}
                              className="ml-1.5 h-8 px-2.5 rounded-md border border-slate-300 text-xs text-slate-600 inline-flex items-center gap-1">
                              <UserCheck size={13} /> {it.assigned_reviewer ? "Reassign" : "Assign"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>

      {assignFor && (
        <ReviewerPickerDialog
          contract={{
            contract_id: assignFor.contract_id,
            assigned_reviewer: assignFor.assigned_reviewer
              ? { user_id: assignFor.assigned_reviewer.user_id, name: assignFor.assigned_reviewer.name,
                  reviewer_role: assignFor.assigned_reviewer.role }
              : null,
          }}
          open={!!assignFor}
          onOpenChange={(v) => !v && setAssignFor(null)}
          onDone={() => { load(); loadCounts(); }} />
      )}
    </AppShell>
  );
}
